/**
 * AllAll 生成接口：Veo 3、Sora 2（FormData 提交，GET 轮询）；图片 Flux 系列（同步 JSON）
 */
import axios from 'axios'
import { getProviderById } from '@/config/ai-providers'
import { buildFormData as buildVeo3FormData, parsePollResponse } from './adapters/video/allall/veo-3-allall'
import { buildFormData as buildVeo31FormData } from './adapters/video/allall/veo-3-1-allall'
import { buildFormData as buildSora2FormData } from './adapters/video/allall/sora-2-allall'
import { buildBody as buildWan25Body, parsePollResponse as parseWan25PollResponse } from './adapters/video/allall/wan-2-5-allall'
import { buildBodyForModel } from './adapters/image/allall'
import { buildBody as buildMidjourneyBody, parsePollResponse as parseMidjourneyPollResponse } from './adapters/image/allall/midjourney'
import { buildBody as buildGptImageBody, parseImageGenerationsItems } from './adapters/image/allall/gpt-image-1'
import { uploadFile } from '../upload'
import { buildBody as buildGemini3ProImageBody, parseResponse as parseGemini3ProImageResponse } from './adapters/image/allall/gemini-3-pro-image-preview'
import { buildBody as buildSpeechBody } from './adapters/audio/allall/speech'
import { buildBody as buildSunoMusicBody, parseSubmitResponse as parseSunoMusicSubmitResponse } from './adapters/audio/allall/suno-music'
import { getAiClient } from './client'
import { extractApiErrorMessage } from './extract-api-error-message'

var POLL_INTERVAL = 3000
var POLL_TIMEOUT = 10 * 60 * 1000

/**
 * 提交 AllAll Veo 3 任务（FormData，非 JSON）
 * AllAll Veo 3 仅支持图片转视频，必须上传图片（File）
 * @returns {Promise<{ taskId: string, provider: 'allall' }>}
 */
export function submitAllallVeo3(params) {
  if (!params.referenceImage || typeof File === 'undefined' || !(params.referenceImage instanceof File)) {
    return Promise.reject(new Error('AllAll Veo 3 仅支持图片转视频，请上传图片'))
  }
  var provider = getProviderById('allall')
  if (!provider || !provider.baseURL) return Promise.reject(new Error('AllAll not configured'))
  var auth = typeof provider.getAuthorization === 'function' ? provider.getAuthorization() : ''
  if (!auth) return Promise.reject(new Error('AllAll API Key not set'))
  var baseURL = provider.baseURL.replace(/\/$/, '')
  var formData = buildVeo3FormData(params)
  return axios.post(baseURL + '/v1/videos', formData, {
    headers: { Authorization: auth },
    timeout: 120000
  }).then(function (res) {
    var data = res && res.data
    var taskId = data && data.id
    if (!taskId) return Promise.reject(new Error('AllAll 未返回 id'))
    return { taskId: taskId, provider: 'allall' }
  }).catch(function (err) {
    var msg = extractApiErrorMessage(err.response && err.response.data) || err.message || '请求失败'
    return Promise.reject(new Error(msg))
  })
}

/**
 * 提交 AllAll Veo 3.1 任务（FormData，与 Veo 3 同接口，model=veo3.1）
 * 仅支持图片转视频，必须上传图片（File）
 * @returns {Promise<{ taskId: string, provider: 'allall' }>}
 */
export function submitAllallVeo31(params) {
  if (!params.referenceImage || typeof File === 'undefined' || !(params.referenceImage instanceof File)) {
    return Promise.reject(new Error('AllAll Veo 3.1 仅支持图片转视频，请上传图片'))
  }
  var provider = getProviderById('allall')
  if (!provider || !provider.baseURL) return Promise.reject(new Error('AllAll not configured'))
  var auth = typeof provider.getAuthorization === 'function' ? provider.getAuthorization() : ''
  if (!auth) return Promise.reject(new Error('AllAll API Key not set'))
  var baseURL = provider.baseURL.replace(/\/$/, '')
  var formData = buildVeo31FormData(params)
  return axios.post(baseURL + '/v1/videos', formData, {
    headers: { Authorization: auth },
    timeout: 120000
  }).then(function (res) {
    var data = res && res.data
    var taskId = data && data.id
    if (!taskId) return Promise.reject(new Error('AllAll 未返回 id'))
    return { taskId: taskId, provider: 'allall' }
  }).catch(function (err) {
    var msg = extractApiErrorMessage(err.response && err.response.data) || err.message || '请求失败'
    return Promise.reject(new Error(msg))
  })
}

/**
 * 轮询 AllAll 任务状态 GET /v1/videos/{id}
 */
export function pollAllallStatus(taskId, callback, interval) {
  var pollInterval = interval || POLL_INTERVAL
  var deadline = Date.now() + POLL_TIMEOUT
  var pollTimer = null
  var stopped = false
  var client = getAiClient('allall')
  var path = '/v1/videos/' + encodeURIComponent(taskId)

  function stop() {
    stopped = true
    if (pollTimer) {
      clearTimeout(pollTimer)
      pollTimer = null
    }
  }

  function poll() {
    if (stopped) return
    client.get(path)
      .then(function (res) {
        if (stopped) return
        var parsed = parsePollResponse(res)
        if (typeof callback === 'function') callback(null, parsed)
        if (parsed.status === 'completed' || parsed.status === 'failed') {
          stop()
          return
        }
        if (Date.now() > deadline) {
          if (typeof callback === 'function') callback(new Error('轮询超时'), null)
          stop()
          return
        }
        pollTimer = setTimeout(poll, pollInterval)
      })
      .catch(function (err) {
        if (stopped) return
        if (typeof callback === 'function') callback(err, null)
        stop()
      })
  }

  poll()
  return { stop: stop }
}

/**
 * 提交 AllAll Sora 2 任务（FormData）
 * AllAll Sora 2 仅支持图片转视频，必须上传图片（File）
 * @returns {Promise<{ taskId: string, provider: 'allall' }>}
 */
export function submitAllallSora2(params) {
  if (!params.referenceImage || typeof File === 'undefined' || !(params.referenceImage instanceof File)) {
    return Promise.reject(new Error('AllAll Sora 2 仅支持图片转视频，请上传图片'))
  }
  var provider = getProviderById('allall')
  if (!provider || !provider.baseURL) return Promise.reject(new Error('AllAll not configured'))
  var auth = typeof provider.getAuthorization === 'function' ? provider.getAuthorization() : ''
  if (!auth) return Promise.reject(new Error('AllAll API Key not set'))
  var baseURL = provider.baseURL.replace(/\/$/, '')
  var formData = buildSora2FormData(params)
  return axios.post(baseURL + '/v1/videos', formData, {
    headers: { Authorization: auth },
    timeout: 120000
  }).then(function (res) {
    var data = res && res.data
    var taskId = data && data.id
    if (!taskId) return Promise.reject(new Error('AllAll 未返回 id'))
    return { taskId: taskId, provider: 'allall' }
  }).catch(function (err) {
    var msg = extractApiErrorMessage(err.response && err.response.data) || err.message || '请求失败'
    return Promise.reject(new Error(msg))
  })
}

/**
 * 提交 AllAll Wan 2.5 任务（JSON，POST /v1/video/generations）
 * 需要 referenceImage 为 URL 或 Base64 字符串
 * @returns {Promise<{ taskId: string, provider: 'allall', allallApi: 'video/generations' }>}
 */
export function submitAllallWan25(params) {
  var provider = getProviderById('allall')
  if (!provider || !provider.baseURL) return Promise.reject(new Error('AllAll not configured'))
  var auth = typeof provider.getAuthorization === 'function' ? provider.getAuthorization() : ''
  if (!auth) return Promise.reject(new Error('AllAll API Key not set'))
  var baseURL = provider.baseURL.replace(/\/$/, '')
  var body = buildWan25Body(params)
  return axios.post(baseURL + '/v1/video/generations', body, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: auth
    },
    timeout: 120000
  }).then(function (res) {
    var data = res && res.data
    var taskId = data && data.id
    if (!taskId) return Promise.reject(new Error('AllAll Wan 2.5 未返回 id'))
    return { taskId: taskId, provider: 'allall', allallApi: 'video/generations' }
  }).catch(function (err) {
    var msg = extractApiErrorMessage(err.response && err.response.data) || err.message || '请求失败'
    return Promise.reject(new Error(msg))
  })
}

/**
 * 轮询 AllAll Wan 2.5 任务状态 GET /v1/video/generations/{id}
 */
export function pollAllallWan25Status(taskId, callback, interval) {
  var pollInterval = interval || POLL_INTERVAL
  var deadline = Date.now() + POLL_TIMEOUT
  var pollTimer = null
  var stopped = false
  var client = getAiClient('allall')
  var path = '/v1/video/generations/' + encodeURIComponent(taskId)

  function stop() {
    stopped = true
    if (pollTimer) {
      clearTimeout(pollTimer)
      pollTimer = null
    }
  }

  function poll() {
    if (stopped) return
    client.get(path)
      .then(function (res) {
        if (stopped) return
        var parsed = parseWan25PollResponse(res)
        if (typeof callback === 'function') callback(null, parsed)
        if (parsed.status === 'completed' || parsed.status === 'failed') {
          stop()
          return
        }
        if (Date.now() > deadline) {
          if (typeof callback === 'function') callback(new Error('轮询超时'), null)
          stop()
          return
        }
        pollTimer = setTimeout(poll, pollInterval)
      })
      .catch(function (err) {
        if (stopped) return
        if (typeof callback === 'function') callback(err, null)
        stop()
      })
  }

  poll()
  return { stop: stop }
}

/**
 * 提交 AllAll Midjourney 任务（JSON，POST /mj/submit/imagine）
 * @returns {Promise<{ taskId: string, provider: 'allall', allallApi: 'mj' }>}
 */
export function submitAllallMidjourney(params) {
  var provider = getProviderById('allall')
  if (!provider || !provider.baseURL) return Promise.reject(new Error('AllAll not configured'))
  var auth = typeof provider.getAuthorization === 'function' ? provider.getAuthorization() : ''
  if (!auth) return Promise.reject(new Error('AllAll API Key not set'))
  var baseURL = provider.baseURL.replace(/\/$/, '')
  var body = buildMidjourneyBody(params)
  return axios.post(baseURL + '/mj/submit/imagine', body, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: auth
    },
    timeout: 60000
  }).then(function (res) {
    var data = res && res.data
    if (data && data.code !== 1) {
      return Promise.reject(new Error(extractApiErrorMessage(data) || 'Midjourney 提交失败'))
    }
    var taskId = data && data.result
    if (!taskId) return Promise.reject(new Error('AllAll Midjourney 未返回 result'))
    return { taskId: String(taskId), provider: 'allall', allallApi: 'mj' }
  }).catch(function (err) {
    var msg = extractApiErrorMessage(err.response && err.response.data) || err.message || '请求失败'
    return Promise.reject(new Error(msg))
  })
}

/**
 * 轮询 AllAll Midjourney 任务状态 GET /mj/task/{taskId}/fetch
 */
export function pollAllallMidjourneyStatus(taskId, callback, interval) {
  var pollInterval = interval || POLL_INTERVAL
  var deadline = Date.now() + POLL_TIMEOUT
  var pollTimer = null
  var stopped = false
  var client = getAiClient('allall')
  var path = '/mj/task/' + encodeURIComponent(taskId) + '/fetch'

  function stop() {
    stopped = true
    if (pollTimer) {
      clearTimeout(pollTimer)
      pollTimer = null
    }
  }

  function poll() {
    if (stopped) return
    client.get(path)
      .then(function (res) {
        if (stopped) return
        var parsed = parseMidjourneyPollResponse(res)
        if (typeof callback === 'function') callback(null, parsed)
        if (parsed.status === 'completed' || parsed.status === 'failed') {
          stop()
          return
        }
        if (Date.now() > deadline) {
          if (typeof callback === 'function') callback(new Error('轮询超时'), null)
          stop()
          return
        }
        pollTimer = setTimeout(poll, pollInterval)
      })
      .catch(function (err) {
        if (stopped) return
        if (typeof callback === 'function') callback(err, null)
        stop()
      })
  }

  poll()
  return { stop: stop }
}

/**
 * AllAll 图片生成（同步）：POST /v1/images/generations，直接返回 data[].url，无需轮询
 * @param {string} modelId - flux-kontext-max | flux-kontext-pro | flux-pro-1.1-ultra | flux-schnell
 * @param {Object} params - prompt(必填), aspectRatio/aspect_ratio, n, size, response_format
 * @returns {Promise<{ urls: string[], provider: 'allall', sync: true }>}
 */
export function generateAllallImage(modelId, params) {
  var provider = getProviderById('allall')
  if (!provider || !provider.baseURL) return Promise.reject(new Error('AllAll not configured'))
  var auth = typeof provider.getAuthorization === 'function' ? provider.getAuthorization() : ''
  if (!auth) return Promise.reject(new Error('AllAll API Key not set'))
  var baseURL = provider.baseURL.replace(/\/$/, '')
  var body = buildBodyForModel(modelId, params)
  if (!body) return Promise.reject(new Error('Unsupported AllAll image model: ' + modelId))
  return axios.post(baseURL + '/v1/images/generations', body, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: auth
    },
    // Flux/相关 AllAll 图片生成：请求超时改为 30 分钟
    timeout: 1800000
  }).then(function (res) {
    var data = res && res.data
    var list = (data && data.data) || []
    var urls = list.map(function (item) { return item && item.url }).filter(Boolean)
    return { urls: urls, provider: 'allall', sync: true }
  }).catch(function (err) {
    var msg = extractApiErrorMessage(err.response && err.response.data) || err.message || '请求失败'
    return Promise.reject(new Error(msg))
  })
}

export { buildBodyForModel }

/**
 * 提交 Suno 音乐任务：POST /suno/submit/music
 * @param {Object} params - prompt/lyrics, mv, title, tags, make_instrumental, task_id, continue_at, continue_clip_id, gpt_description_prompt, notify_hook, task
 * @returns {Promise<{ taskId: string, provider: 'allall', allallApi: 'suno/music' }>}
 */
export function submitSunoMusic(params) {
  var provider = getProviderById('allall')
  if (!provider || !provider.baseURL) return Promise.reject(new Error('AllAll not configured'))
  var auth = typeof provider.getAuthorization === 'function' ? provider.getAuthorization() : ''
  if (!auth) return Promise.reject(new Error('AllAll API Key not set'))
  var baseURL = provider.baseURL.replace(/\/$/, '')
  var body = buildSunoMusicBody(params)
  return axios.post(baseURL + '/suno/submit/music', body, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: auth
    },
    timeout: 60000
  }).then(function (res) {
    var taskId = parseSunoMusicSubmitResponse(res)
    if (!taskId) {
      var msg = extractApiErrorMessage(res.data) || 'Suno 未返回任务 ID'
      return Promise.reject(new Error(msg))
    }
    return { taskId: taskId, provider: 'allall', allallApi: 'suno/music' }
  }).catch(function (err) {
    var msg = extractApiErrorMessage(err.response && err.response.data) || err.message || '请求失败'
    return Promise.reject(new Error(msg))
  })
}

/**
 * 轮询 Suno 音乐任务状态 GET /suno/fetch/{task_id}
 * 响应：{ code: 'success', data: { status: 'SUCCESS'|'IN_PROGRESS', data: [ { audio_url, image_url, title, prompt, tags, duration, ... } ], fail_reason } }
 * @param {string} taskId
 * @param {Function} callback(err, { status, tracks, error })
 * @param {number} interval
 */
export function pollSunoMusicStatus(taskId, callback, interval) {
  var pollInterval = interval || POLL_INTERVAL
  var deadline = Date.now() + POLL_TIMEOUT
  var pollTimer = null
  var stopped = false
  var client = getAiClient('allall')
  var path = '/suno/fetch/' + encodeURIComponent(taskId)

  function stop() {
    stopped = true
    if (pollTimer) {
      clearTimeout(pollTimer)
      pollTimer = null
    }
  }

  function poll() {
    if (stopped) return
    client.get(path)
      .then(function (body) {
        if (stopped) return
        if (!body || body.code !== 'success') {
          var msg = extractApiErrorMessage(body) || '请求失败'
          if (typeof callback === 'function') callback(null, { status: 'failed', tracks: null, error: msg })
          stop()
          return
        }
        var inner = body.data
        if (!inner || typeof inner !== 'object') {
          var msg2 = extractApiErrorMessage(body) || '请求失败'
          if (typeof callback === 'function') callback(null, { status: 'failed', tracks: null, error: msg2 })
          stop()
          return
        }
        var taskStatus = (inner.status || '').toUpperCase()
        var clips = Array.isArray(inner.data) ? inner.data : []
        var failReason = (inner.fail_reason && String(inner.fail_reason).trim()) || ''

        if (taskStatus === 'SUCCESS') {
          if (typeof callback === 'function') callback(null, { status: 'completed', tracks: clips, error: null })
          stop()
          return
        }
        if (taskStatus === 'FAILED' || taskStatus === 'FAILURE') {
          if (typeof callback === 'function') callback(null, { status: 'failed', tracks: null, error: failReason || extractApiErrorMessage(body) || '生成失败' })
          stop()
          return
        }
        if (typeof callback === 'function') callback(null, { status: 'pending', tracks: null })
        scheduleNext()
      })
      .catch(function (err) {
        if (stopped) return
        if (typeof callback === 'function') callback(err, null)
        stop()
      })

    function scheduleNext() {
      if (stopped) return
      if (Date.now() > deadline) {
        if (typeof callback === 'function') callback(new Error('轮询超时'), null)
        stop()
        return
      }
      pollTimer = setTimeout(poll, pollInterval)
    }
  }

  poll()
  return { stop: stop }
}

/**
 * AllAll 语音合成（同步）：POST /v1/audio/speech，model 固定 speech-2.6-hd
 * @param {Object} params - input/text/prompt(必填), voice, voice_setting, pronunciation_dict, audio_setting, subtitle_enable
 * @returns {Promise<{ url: string, provider: 'allall', sync: true }>} 若接口返回 JSON 含 url 则用 url，否则为音频二进制时返回 blob URL
 */
export function generateAllallSpeech(params) {
  var provider = getProviderById('allall')
  if (!provider || !provider.baseURL) return Promise.reject(new Error('AllAll not configured'))
  var auth = typeof provider.getAuthorization === 'function' ? provider.getAuthorization() : ''
  if (!auth) return Promise.reject(new Error('AllAll API Key not set'))
  var baseURL = provider.baseURL.replace(/\/$/, '')
  var body = buildSpeechBody(params)
  return axios.post(baseURL + '/v1/audio/speech', body, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: auth
    },
    responseType: 'arraybuffer',
    timeout: 60000
  }).then(function (res) {
    var contentType = (res.headers && res.headers['content-type']) || ''
    var data = res.data
    if (contentType.indexOf('application/json') !== -1 && data && typeof data.byteLength !== 'undefined') {
      var str = typeof TextDecoder !== 'undefined' ? new TextDecoder().decode(data) : String.fromCharCode.apply(null, new Uint8Array(data))
      var json = {}
      try { json = JSON.parse(str) } catch (e) {}
      var url = json.url || (json.data && json.data.url)
      if (url) return { url: url, provider: 'allall', sync: true }
      if (json.data && typeof json.data === 'string') return { url: 'data:audio/mpeg;base64,' + json.data, provider: 'allall', sync: true }
    }
    if (data && typeof data.byteLength !== 'undefined' && data.byteLength > 0) {
      var blob = new Blob([data], { type: contentType.indexOf('audio') !== -1 ? contentType : 'audio/mpeg' })
      var blobUrl = typeof URL !== 'undefined' && URL.createObjectURL ? URL.createObjectURL(blob) : null
      if (blobUrl) return { url: blobUrl, provider: 'allall', sync: true }
    }
    return Promise.reject(new Error('AllAll 语音未返回有效音频'))
  }).catch(function (err) {
    var msg = extractApiErrorMessage(err.response && err.response.data) || err.message || '请求失败'
    if (err.response && err.response.data && err.response.data.byteLength) {
      try {
        var s = typeof TextDecoder !== 'undefined' ? new TextDecoder().decode(err.response.data) : ''
        var o = JSON.parse(s)
        var fromBuf = extractApiErrorMessage(o)
        if (fromBuf) msg = fromBuf
      } catch (e) {}
    }
    return Promise.reject(new Error(msg))
  })
}

function normalizeBase64ForAtob(b64) {
  var s = String(b64).replace(/\s/g, '')
  var pad = s.length % 4
  if (pad) s += '===='.slice(0, 4 - pad)
  return s
}

/**
 * AllAll gpt-image-1（同步）：POST /v1/images/generations。
 * data[].url 原样返回；data[].b64_json 转为 data:image/png;base64,...，由前端 fetch 得 Blob 后本地 saveMediaBlob，不再上传业务接口。
 * @param {Object} params - prompt、imageSize(1:1|3:2|2:3)、imageCount
 * @returns {Promise<{ urls: string[], provider: 'allall', sync: true }>}
 */
export function generateAllallGptImage(params) {
  var provider = getProviderById('allall')
  if (!provider || !provider.baseURL) return Promise.reject(new Error('AllAll not configured'))
  var auth = typeof provider.getAuthorization === 'function' ? provider.getAuthorization() : ''
  if (!auth) return Promise.reject(new Error('AllAll API Key not set'))
  var baseURL = provider.baseURL.replace(/\/$/, '')
  var body = buildGptImageBody(params)
  return axios.post(baseURL + '/v1/images/generations', body, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: auth
    },
    // gpt-image-1 图片生成：请求超时改为 30 分钟
    timeout: 1800000
  }).then(function (res) {
    var items = parseImageGenerationsItems(res)
    if (!items.length) {
      return Promise.reject(new Error('AllAll 未返回图片数据（无 b64_json/url）'))
    }
    var urls = []
    for (var idx = 0; idx < items.length; idx++) {
      var item = items[idx]
      if (item.url) {
        urls.push(item.url)
        continue
      }
      if (item.b64) {
        urls.push('data:image/png;base64,' + normalizeBase64ForAtob(item.b64))
        continue
      }
      return Promise.reject(new Error('无效的图片项'))
    }
    return {
      urls: urls,
      provider: 'allall',
      sync: true
    }
  }).catch(function (err) {
    var msg = extractApiErrorMessage(err.response && err.response.data) || err.message || '请求失败'
    return Promise.reject(new Error(msg))
  })
}

/**
 * AllAll：POST /v1/chat/completions
 * 固定 model=gemini-3-pro-image-preview、max_tokens=4096；仅 imageUrl 与提示词可变（mode 切换默认文案或传 text 覆盖）
 * @param {Object} params
 * @param {string} params.imageUrl - data:image/...;base64,... 或图片 URL
 * @param {string} [params.mode] - 'watermark' | 'remove_background'（见 gemini-3-pro-image-preview.js 中 GEMINI_IMAGE_EDIT_MODE）
 * @param {string} [params.text] - 自定义 user 文本提示，传入则不用 mode 默认文案
 * @returns {Promise<{ provider: 'allall', sync: true, raw: any, text: string, imageUrls: string[] }>}
 */
export function callAllallGemini3ProImagePreview(params) {
  var provider = getProviderById('allall')
  if (!provider || !provider.baseURL) return Promise.reject(new Error('AllAll not configured'))
  var auth = typeof provider.getAuthorization === 'function' ? provider.getAuthorization() : ''
  if (!auth) return Promise.reject(new Error('AllAll API Key not set'))
  var baseURL = provider.baseURL.replace(/\/$/, '')
  var body = buildGemini3ProImageBody(params)
  return axios.post(baseURL + '/v1/chat/completions', body, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: auth
    },
    timeout: 120000
  }).then(function (res) {
    var parsed = parseGemini3ProImageResponse(res)
    return {
      provider: 'allall',
      sync: true,
      raw: parsed.data,
      text: parsed.text,
      imageUrls: parsed.imageUrls
    }
  }).catch(function (err) {
    var msg = extractApiErrorMessage(err.response && err.response.data) || err.message || '请求失败'
    return Promise.reject(new Error(msg))
  })
}

